import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { components } from '@bookingdei/api-client/types';
import { api } from '../api';
import { useLanguage } from './language-context';
import { useResource } from './use-resource';
import { demoArea } from '../theme/tokens';
import { AccountMenu } from './account-menu';
import { BrandLockup } from './brand-lockup';
import { HeaderSearchPill } from './header-search-pill';
import { InnerAppBar } from './inner-app-bar';
import { NavDrawer } from './nav-drawer';
import { NavMenu } from './nav-menu';
import { ThemeSwitch } from './theme-switch';
import { MOBILE_QUERY, useMediaQuery } from './use-media-query';

type CategoryList = components['schemas']['CategoryList'];

// The site header (design DC:106-220, visibility rules DC:4871-4920).
//
// Desktop (≥768): lockup · [search pill on search/business routes] · centre nav
// (≥1150 only, and never on search/business) · theme switch, sign-in or the signed-in
// account menu, and the "Add your business FREE" CTA — which between 768 and 1149
// collapses to a compact "Add business" plus a ☰ that opens the nav drawer.
//
// Mobile (<768): the home route gets the lockup + location pill + theme switch; every
// other route gets the inner app bar (‹ back, title, contextual action). The mobile
// bar hides on scroll-down and returns on scroll-up; the desktop header never hides.
//
// The centre nav item reads "Booking" deliberately — v3's audit records that the
// client confirmed this label over "Explore". The internal routes are /offer, /search.
// Centre nav per the "Videos page — final" design: Offers % opens Categories (the
// registry, each to its offers) and Popular (Live offers, Trending now, Offer reels NEW);
// Booking opens Categories (each to its search results) and Popular (Trending now, Near
// you, Top rated, New this week — search sorts); Videos NEW; Resources ▾.

// Routes where the header shows the search pill instead of the centre nav.
export function isPillRoute(pathname: string): boolean {
  return /^\/search\b/.test(pathname) || /^\/(?:business|b)\/[^/]+\/?$/.test(pathname);
}

export function AppBar() {
  const { t, lang } = useLanguage();
  const { pathname } = useLocation();
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const [hidden, setHidden] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // Categories drive the Booking dropdown and the drawer, so the menu follows the
  // registry rather than a hardcoded list (FR-L6: no code release to add one).
  const categories = useResource<CategoryList>((signal) => api.GET('/registries/categories', { signal, lang }), [lang]);

  // Hide-on-scroll, mobile only (design DC:4126-4134): 6 px hysteresis so jitter does
  // not flap the bar, and it always shows within 90 px of the top.
  useEffect(() => {
    if (!isMobile) {
      setHidden(false);
      return;
    }
    let last = window.scrollY;
    function onScroll() {
      const y = window.scrollY;
      if (y > last + 6 && y > 90) setHidden(true);
      else if (y < last - 6 || y < 90) setHidden(false);
      last = y;
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isMobile]);

  // A route change closes the drawer and brings the bar back.
  useEffect(() => {
    setDrawerOpen(false);
    setHidden(false);
  }, [pathname]);

  const categoryItems = (categories.data?.items ?? []).slice(0, 8).map((c) => ({
    label: (lang === 'bn' ? c.label_bn : c.label_en) ?? c.label_en,
    to: `/offer?category=${encodeURIComponent(c.id)}`,
  }));

  const popularItems = [
    { label: t('nav.popular_offers'), to: '/offer' },
    { label: t('nav.popular_trending'), to: '/trending' },
    { label: t('nav.popular_offer_reels'), to: '/videos#vd-offers', badge: t('nav.new_badge') },
  ];

  const bookingCategoryItems = (categories.data?.items ?? []).slice(0, 8).map((c) => ({
    label: (lang === 'bn' ? c.label_bn : c.label_en) ?? c.label_en,
    to: `/search?category=${encodeURIComponent(c.id)}`,
  }));

  // "Near you" is plain search: results already follow the visitor's location, and a
  // distance sort is deferred (DR-44).
  const bookingPopularItems = [
    { label: t('nav.popular_trending'), to: '/search?sort=trending' },
    { label: t('nav.popular_near_you'), to: '/search' },
    { label: t('nav.popular_top_rated'), to: '/search?sort=rating' },
    { label: t('nav.popular_new_week'), to: '/search?sort=newest' },
  ];

  const resourceItems = [
    { label: t('nav.res_for_business'), to: '/for-business' },
    { label: t('nav.res_blog'), to: '/blog' },
    { label: t('nav.res_help'), to: '/help' },
  ];

  const pill = isPillRoute(pathname);
  const offersActive = /^\/offer\b/.test(pathname);
  const videosActive = /^\/videos\b/.test(pathname);
  const onForBusiness = /^\/for-business\/?$/.test(pathname);

  return (
    <>
      <header className="hdr" data-hide={hidden || undefined}>
        {isMobile ? (
          pathname === '/' ? (
            <div className="mbar">
              <div className="mbar__left">
                <BrandLockup className="mbar__brand" />
                <span className="hdr__loc">
                  <span className="hdr__loc__pin" aria-hidden="true">
                    📍
                  </span>
                  {demoArea[lang] ?? demoArea.en}
                </span>
              </div>
              <ThemeSwitch size="sm" />
            </div>
          ) : (
            <InnerAppBar />
          )
        ) : (
          <div className="hdr__inner">
            <BrandLockup />
            {pill && <HeaderSearchPill />}
            {!pill && (
              <nav className="hdr__center" aria-label={t('nav.primary')}>
                <NavMenu
                  label={t('nav.offers')}
                  to="/offer"
                  caret={false}
                  active={offersActive}
                  adornment={
                    <span className="hdr__offersbadge" aria-hidden="true">
                      %
                    </span>
                  }
                  groups={[
                    { title: t('nav.categories'), items: categoryItems },
                    { title: t('nav.popular'), items: popularItems },
                  ]}
                />
                <NavMenu
                  label={t('nav.booking')}
                  to="/search"
                  caret={false}
                  groups={[
                    { title: t('nav.categories'), items: bookingCategoryItems },
                    { title: t('nav.popular'), items: bookingPopularItems },
                  ]}
                />
                <Link
                  className={videosActive ? 'hdr__link hdr__link--active' : 'hdr__link'}
                  to="/videos"
                  aria-current={videosActive ? 'page' : undefined}
                >
                  {t('nav.videos')}
                  <span className="hdr__new" aria-hidden="true">
                    {t('nav.new_badge')}
                  </span>
                </Link>
                <NavMenu label={t('nav.resources')} groups={[{ items: resourceItems }]} />
              </nav>
            )}

            <div className="hdr__right">
              <ThemeSwitch />
              <AccountMenu />
              {/* The "Add your business" CTA only links to /for-business, so on that page
                  it is a confusing self-link and is left out (the rest stays). */}
              {!onForBusiness && (
                <>
                  <Link className="hdr__addbiz" to="/for-business">
                    {t('nav.add_business')}
                    <span className="hdr__free" aria-hidden="true">
                      {t('nav.free')}
                    </span>
                  </Link>
                  <Link className="hdr__addbiz-compact" to="/for-business">
                    {t('nav.add_business_short')}
                  </Link>
                </>
              )}
              <button
                type="button"
                className="hdr__burger"
                aria-label={t('nav.menu')}
                aria-haspopup="dialog"
                aria-expanded={drawerOpen}
                onClick={() => setDrawerOpen(true)}
              >
                <span aria-hidden="true">☰</span>
              </button>
            </div>
          </div>
        )}
      </header>
      {/* Outside the header: its backdrop-filter would otherwise become the containing
          block for the drawer's fixed positioning. */}
      <NavDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        categoryItems={categoryItems}
        popularItems={popularItems}
        bookingCategoryItems={bookingCategoryItems}
        bookingPopularItems={bookingPopularItems}
        resourceItems={resourceItems}
      />
    </>
  );
}
